import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';

const STATUS_BADGE = {
  SENT: 'inc-badge--success',
  PENDING: 'inc-badge--warn',
  FAILED_RECIPIENT: 'inc-badge--fail',
  INVALID: 'inc-badge--fail',
  SKIPPED_DUPLICATE: 'inc-badge--fail',
};

function uploadResultMessage(r) {
  const parts = [`Added ${r.added} new recipient(s)`];
  const notes = [];
  if (r.skippedDuplicates) notes.push(`${r.skippedDuplicates} already on this campaign`);
  if (r.skippedCrossCampaign) notes.push(`${r.skippedCrossCampaign} already targeted in another campaign`);
  if (notes.length) parts.push(`(skipped: ${notes.join(', ')})`);
  if (r.found != null) parts.push(`— ${r.found} total found`);
  return parts.join(' ') + '.';
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pasteText, setPasteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [sheetOptions, setSheetOptions] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const loadCampaign = useCallback(() => {
    api.campaigns.detail(id).then(setCampaign).catch(e => setMsg(e.message));
  }, [id]);

  const loadRecipients = useCallback(() => {
    api.campaigns.recipients(id, { page, pageSize: 50, ...(statusFilter ? { status: statusFilter } : {}) })
      .then(r => { setRecipients(r.rows); setTotal(r.total); })
      .catch(() => {});
  }, [id, page, statusFilter]);

  useEffect(() => { loadCampaign(); loadRecipients(); }, [loadCampaign, loadRecipients]);

  // Poll while running so progress updates live without a manual refresh.
  useEffect(() => {
    if (campaign?.isRunning) {
      pollRef.current = setInterval(() => { loadCampaign(); loadRecipients(); }, 4000);
      return () => clearInterval(pollRef.current);
    }
    return undefined;
  }, [campaign?.isRunning, loadCampaign, loadRecipients]);

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    setMsg('');
    setSheetOptions(null);
    try {
      const r = await api.campaigns.uploadRecipients(id, file);
      if (r.needsSheetSelection) {
        // Workbook has multiple sheets — hold onto the file and ask which one to use.
        setPendingFile(file);
        setSheetOptions(r.sheets);
        setSelectedSheet(r.sheets[0]);
        setMsg('');
        return;
      }
      setMsg(uploadResultMessage(r));
      loadCampaign(); loadRecipients();
    } catch (err) { setMsg(err.message); }
    finally { setBusy(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const confirmSheetUpload = async () => {
    if (!pendingFile || !selectedSheet) return;
    setBusy(true);
    setMsg('');
    try {
      const r = await api.campaigns.uploadRecipients(id, pendingFile, selectedSheet);
      setMsg(uploadResultMessage(r));
      loadCampaign(); loadRecipients();
    } catch (err) { setMsg(err.message); }
    finally {
      setBusy(false);
      setPendingFile(null);
      setSheetOptions(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const cancelSheetUpload = () => {
    setPendingFile(null);
    setSheetOptions(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submitPaste = async () => {
    if (!pasteText.trim()) return;
    setBusy(true);
    try {
      const r = await api.campaigns.pasteRecipients(id, pasteText);
      setMsg(uploadResultMessage(r));
      setPasteText('');
      loadCampaign(); loadRecipients();
    } catch (err) { setMsg(err.message); }
    finally { setBusy(false); }
  };

  const start = async () => {
    const pendingCount = counts.PENDING || 0;
    const activeSenders = campaign.activeSenderCount ?? 0;
    if (pendingCount === 0) return;
    const ok = window.confirm(
      `You're about to email ${pendingCount} recipient(s) using ${activeSenders} active sender(s).\n\n` +
      (activeSenders === 0 ? 'No active senders are configured — sending will pause immediately.\n\n' : '') +
      'This starts sending for real and cannot be undone once it goes out. Continue?'
    );
    if (!ok) return;
    setBusy(true);
    try { await api.campaigns.start(id); loadCampaign(); }
    catch (err) { setMsg(err.message); }
    finally { setBusy(false); }
  };

  const sendTestEmail = async () => {
    if (!testTo.trim()) { setTestMsg('Enter an address to send the test to.'); return; }
    setTestSending(true);
    setTestMsg('');
    try {
      const r = await api.campaigns.testSend({
        to: testTo.trim(),
        subject: campaign.SUBJECT,
        htmlBody: campaign.HTML_BODY,
        senderDisplayName: campaign.SENDER_DISPLAY_NAME,
        unsubscribeEmail: campaign.UNSUBSCRIBE_EMAIL,
      });
      setTestMsg(`Test sent from ${r.sentFrom}.`);
    } catch (err) {
      setTestMsg(err.message);
    } finally {
      setTestSending(false);
    }
  };

  const pause = async () => {
    setBusy(true);
    try { await api.campaigns.pause(id); loadCampaign(); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this campaign and all its recipient records? This cannot be undone.')) return;
    await api.campaigns.remove(id);
    navigate('/campaigns');
  };

  if (!campaign) return <div className="page-loading"><div className="spinner" /></div>;

  const counts = campaign.counts || {};
  const totalRecipients = (counts.PENDING || 0) + (counts.SENT || 0) + (counts.FAILED_RECIPIENT || 0) + (counts.INVALID || 0) + (counts.SKIPPED_DUPLICATE || 0);
  const resolved = (counts.SENT || 0) + (counts.FAILED_RECIPIENT || 0) + (counts.INVALID || 0) + (counts.SKIPPED_DUPLICATE || 0);
  const pct = totalRecipients ? Math.round((resolved / totalRecipients) * 100) : 0;

  return (
    <div className="page">
      <button className="btn-back" onClick={() => navigate('/campaigns')}>← All Campaigns</button>

      <div className="page-header">
        <div>
          <div className="page-title">{campaign.NAME}</div>
          <div className="page-desc">{campaign.SUBJECT}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {campaign.isRunning ? (
            <button className="btn-primary" style={{ background: '#E8C547', color: '#1a1a1a' }} disabled={busy} onClick={pause}>
              ⏸ Pause
            </button>
          ) : (
            <button className="btn-primary" disabled={busy || (counts.PENDING || 0) === 0} onClick={start}>
              ▶ {(campaign.STATUS === 'PAUSED' || counts.SENT > 0) ? 'Resume' : 'Start'} Sending
            </button>
          )}
          <button className="btn-danger" disabled={campaign.isRunning} onClick={remove}>Delete</button>
        </div>
      </div>

      <div className="stat-card stat-card--accent" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontWeight: 700 }}>{pct}% complete</span>
          <span style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {counts.SENT || 0} sent · {(counts.FAILED_RECIPIENT || 0) + (counts.INVALID || 0)} failed
            {counts.SKIPPED_DUPLICATE ? ` · ${counts.SKIPPED_DUPLICATE} skipped (already emailed elsewhere)` : ''} · {counts.PENDING || 0} pending
            {campaign.isRunning && <strong style={{ color: '#44BBA4', marginLeft: 8 }}>● sending now</strong>}
          </span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: 'rgba(15,60,96,0.08)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, var(--navy), var(--blue))', transition: 'width .4s' }} />
        </div>
        {!campaign.isRunning && campaign.STATUS === 'PAUSED' && (counts.PENDING || 0) > 0 && (
          campaign.PAUSE_REASON === 'SENDERS_EXHAUSTED' ? (
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 10 }}>
              ⏳ Paused — today's sender capacity is used up. It'll pick back up on its own once your senders reset (checked automatically every 30 min), or click Resume to retry now.
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 10 }}>
              ⏸ Paused by you. It stays paused until you click Resume — it won't restart on its own.
            </div>
          )
        )}
      </div>

      {!campaign.isRunning && (
        <div className="table-card" style={{ marginBottom: 20 }}>
          <div className="table-toolbar"><div className="page-title" style={{ fontSize: 16 }}>Add Recipients</div></div>
          <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label className="field-label">Upload Excel / CSV (auto-detects the email column, e.g. "IEC email")</label>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={uploadFile} disabled={busy} />
              {sheetOptions && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-2)' }}>This file has multiple sheets — pick one:</span>
                  <select className="field-input" style={{ width: 200 }} value={selectedSheet}
                    onChange={e => setSelectedSheet(e.target.value)} disabled={busy}>
                    {sheetOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button className="btn-primary" disabled={busy} onClick={confirmSheetUpload}>Use this sheet</button>
                  <button className="btn-danger" disabled={busy} onClick={cancelSheetUpload}>Cancel</button>
                </div>
              )}
            </div>
            <div>
              <label className="field-label">Or paste addresses (comma / semicolon / newline separated)</label>
              <textarea className="field-input" rows={3} value={pasteText} onChange={e => setPasteText(e.target.value)}
                placeholder="a@example.com, b@example.com" />
              <button className="btn-primary" style={{ marginTop: 8 }} disabled={busy} onClick={submitPaste}>Add Pasted Addresses</button>
            </div>
            <div>
              <label className="field-label">Send yourself a test email before starting</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="field-input" style={{ flex: 1 }} placeholder="you@example.com"
                  value={testTo} onChange={e => setTestTo(e.target.value)} />
                <button className="btn-primary" disabled={testSending} onClick={sendTestEmail}>
                  {testSending ? 'Sending…' : 'Send Test'}
                </button>
              </div>
              {testMsg && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6 }}>{testMsg}</div>}
            </div>
            {msg && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{msg}</div>}
          </div>
        </div>
      )}

      <div className="table-card">
        <div className="table-toolbar">
          <div className="page-title" style={{ fontSize: 16 }}>Recipients ({total})</div>
          <select className="field-input" style={{ marginLeft: 'auto', width: 200 }}
            value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="SENT">Sent</option>
            <option value="FAILED_RECIPIENT">Failed</option>
            <option value="INVALID">Invalid</option>
            <option value="SKIPPED_DUPLICATE">Skipped (duplicate)</option>
          </select>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead><tr><th>Email</th><th>Status</th><th>Sent Via</th><th>Last Error</th><th>Updated</th></tr></thead>
            <tbody>
              {recipients.map(r => (
                <tr key={r.ID}>
                  <td>{r.EMAIL}</td>
                  <td><span className={`inc-badge ${STATUS_BADGE[r.STATUS] || ''}`}>{r.STATUS}</span></td>
                  <td>{r.SENDER_USED || '—'}</td>
                  <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.ERROR_MSG || '—'}</td>
                  <td>{r.UPDATED_AT ? new Date(r.UPDATED_AT).toLocaleString() : '—'}</td>
                </tr>
              ))}
              {recipients.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--text-3)' }}>No recipients yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '12px 20px', alignItems: 'center' }}>
          <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span className="page-info">Page {page}</span>
          <button className="page-btn" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      </div>
    </div>
  );
}
